import * as React from "react";
import { get } from "lodash";
import Context from "./Context";
import { createNewPromise } from "./utils";

const useGoogleReCaptchaController = ({
  containerId,
  sitekey,
  scriptId,
  scriptSrc,
  badge,
  size,
  theme,
  visibility,
  callback,
  version = "v3"
}) => {
  const [visibilityValue, setVisibility] = React.useState(visibility);
  const asyncExecutionPromise = React.useRef(createNewPromise());
  let recaptchaCallback = callback;

  if (!recaptchaCallback && version === "v2") {
    recaptchaCallback = token => {
      asyncExecutionPromise.current.resolve(token);
    };
  }

  const [renderParameters, setRenderOptions] = React.useState({
    sitekey,
    badge,
    size,
    theme,
    callback: recaptchaCallback
  });
  React.useEffect(() => {
    setRenderOptions({
      badge,
      size,
      theme,
      sitekey
    });
  }, [badge, size, theme, sitekey]);
  React.useEffect(() => {
    setVisibility(visibility);
  }, [visibility]);
  const grecaptchaPromise = React.useMemo(createNewPromise, []);
  const executeRecaptcha = React.useCallback(action => {
    return grecaptchaPromise.promise.then(key => {
      return window.grecaptcha.execute(key, {
        action
      });
    });
  }, [grecaptchaPromise.promise]);
  const getResponse = React.useCallback(action => {
    return grecaptchaPromise.promise.then(key => {
      return window.grecaptcha.getResponse(key);
    });
  }, [grecaptchaPromise.promise]);
  const handleRecaptchaWindowClose = React.useCallback(promise => {
    const onClose = () => {
      window.grecaptcha.reset();
      promise.reject(new Error("Recaptcha is required"));
    };

    HTMLCollection.prototype.find = Array.prototype.find;
    const recaptcha = document.getElementsByTagName("iframe").find(x => get(x, "src", "").includes("google.com/recaptcha/api2/bframe"));

    if (recaptcha) {
      const recaptchaWindow = recaptcha.parentNode.parentNode;
      const observer = new MutationObserver(x => {
        if (!recaptchaWindow) return;
        return recaptchaWindow.style.visibility === "hidden" && onClose();
      });
      observer.observe(recaptchaWindow, {
        attributes: true,
        attributeFilter: ["style"]
      });
    }
  }, []);
  const executeRecaptchaAsync = React.useCallback(async () => {
    asyncExecutionPromise.current = createNewPromise();
    const key = await grecaptchaPromise.promise;
    window.grecaptcha.execute(key);
    handleRecaptchaWindowClose(asyncExecutionPromise.current);
    const token = await asyncExecutionPromise.current.promise;
    window.grecaptcha.reset(key);
    return token;
  }, [grecaptchaPromise.promise, handleRecaptchaWindowClose]);
  React.useEffect(() => {
    /**
     * Scripts has already been injected script,
     * return to avoid duplicated scripts
     */
    if (document.getElementById(scriptId)) {
      throw new Error("Google reCaptcha");
    }

    const head = document.getElementsByTagName("head")[0];
    const js = document.createElement("script");
    js.id = scriptId;
    js.src = scriptSrc;
    let interval;

    js.onload = () => {
      window.grecaptcha.ready(() => {
        const container = document.getElementById(containerId);
        const key = window.grecaptcha.render(container, { ...renderParameters,
          "expired-callback": () => callback(null)
        });
        grecaptchaPromise.resolve(key);
        interval = setInterval(() => {
          window.grecaptcha.reset(key, renderParameters);
        }, 1000 * 60 * 60); // 1 hour
      });
    };

    head.appendChild(js);
    return () => {
      const container = document.getElementById(containerId);

      if (container) {
        container.style.visibility = "hidden";
      }

      head.removeChild(js);
      clearInterval(interval);
    };
  }, []);
  React.useEffect(() => {
    if (window.grecaptcha) {
      grecaptchaPromise.promise.then(key => {
        window.grecaptcha.reset(key, renderParameters);
      });
    }
  }, [grecaptchaPromise.promise, renderParameters]);
  React.useEffect(() => {
    const container = document.getElementById(containerId);

    if (container) {
      container.style.visibility = visibilityValue;
    }
  }, [containerId, visibilityValue]);
  return {
    executeRecaptcha,
    setRenderOptions,
    setVisibility,
    getResponse,
    executeRecaptchaAsync
  };
};

let Provider = ({
  children,
  sitekey,
  containerId,
  scriptId,
  scriptSrc,
  size,
  theme,
  badge,
  visibility,
  callback,
  version = "v3"
}, ref) => {
  const contextValue = useGoogleReCaptchaController({
    containerId,
    sitekey,
    scriptId,
    scriptSrc,
    badge,
    size,
    theme,
    visibility,
    callback,
    version
  });
  React.useImperativeHandle(ref, () => contextValue);
  return React.createElement(Context.Provider, {
    value: contextValue
  }, typeof children === "function" ? children(contextValue) : children);
};

Provider = React.forwardRef(Provider);
Provider.defaultProps = {
  containerId: "recaptcha",
  scriptId: "google-recaptcha-v3",
  scriptSrc: `https://www.google.com/recaptcha/api.js?render=explicit`,
  size: "invisible",
  theme: "light",
  badge: "bottomleft",
  visibility: "visible"
};
export default Provider;